import {produce} from "immer";
import {ApolloQueryAdapter, ApolloQueryAdapterArgs} from "./apollo-query-adapter";
import {LoadArgs, SideEffect} from "../infinite-scroller";
import {QueryResult} from "react-apollo";
import {MangoQueryResult} from "../../../common/types/couchdb/mango-query-result";
import React, {Dispatch} from "react";
import {DocumentNode} from "apollo-link";
import {NetworkStatus} from "apollo-boost";
import {cloneDeep} from "lodash";

const CouchFindAdapterInitState = {
    lastBookmark: null as string,
    currentBookmark: null as string,
    prevNetStat: null as NetworkStatus,
    currNetStat: null as NetworkStatus
}

type State = typeof CouchFindAdapterInitState;

type Action = {
    type: "INIT_BOOKMARK"
} |
{
    type: "UPDATE_NETWORK_STATUS",
    status: NetworkStatus
} |
{
    type: 'ON_PAGE_LOADED',
    bookmark: string
}

interface BookmarkAdapterArgs extends ApolloQueryAdapterArgs {
    getResultFromResponse: (response: unknown) => unknown,
    dataFieldName?: string,
    bookmarkFieldName?: string
}

/**
 * Adapter for CouchDB Mango queries that use bookmarks for pagination.
 */
export class BookmarkAdapter extends ApolloQueryAdapter<State, Action> {
    initState = CouchFindAdapterInitState;
    sideEffect: SideEffect;
    getLoading = (queryResult: QueryResult<unknown>) => {
        return queryResult.networkStatus === NetworkStatus.fetchMore ||
            queryResult.networkStatus === NetworkStatus.loading;
    }
    getMangoQueryResult: (data: unknown) => MangoQueryResult;

    dataFieldName = "docs";
    bookmarkFieldName = "bookmark";

    constructor(args: BookmarkAdapterArgs) {
        super(args);
        this.getMangoQueryResult = args.getResultFromResponse;
        if (args.dataFieldName){
            this.dataFieldName = args.dataFieldName;
        }
        if (args.bookmarkFieldName){
            this.bookmarkFieldName = args.bookmarkFieldName;
        }
    }

    /**
     * State machine used for keeping track of the current bookmark and the last bookmark, and tracks query NetworkStatus.
     * Eventually the currentBookmark and lastBookmark will be compared to check if the last page has been reached.
     */
    reducer = (state: State, action: Action) => {
        switch (action.type){
            case "ON_PAGE_LOADED":
                return produce(state, (draft) => {
                    draft.lastBookmark = draft.currentBookmark;
                    draft.currentBookmark = action[this.bookmarkFieldName];
                })
            case "INIT_BOOKMARK":
                return produce(state, (draft) => {
                    draft.lastBookmark = null;
                    draft.currentBookmark = null;
                });
            case "UPDATE_NETWORK_STATUS":
                return produce(state, (draft) => {
                    draft.prevNetStat = draft.currNetStat;
                    draft.currNetStat = action.status;
                })
            default:
                return state;
        }
    }

    onLoadMore = (state: State, dispatch: React.Dispatch<Action>) => {
        this.queryResult.fetchMore({
            variables: {
                ...this.queryResult.variables,
                [this.bookmarkFieldName]: state.currentBookmark
            },
            query: this.query,

            // When the next page is fetched, update the cached query. This will
            // subsequently update the UI subscribed to this cache as well.
            updateQuery: (prevData: any, args) => {
                const prevClone = cloneDeep(prevData);

                const fetchMoreData = args.fetchMoreResult;
                const prevResult = this.getMangoQueryResult(prevClone);
                const currResult = this.getMangoQueryResult(fetchMoreData);
                prevResult[this.dataFieldName].push(...currResult[this.dataFieldName]);
                prevResult[this.bookmarkFieldName] = currResult[this.bookmarkFieldName];

                dispatch({ type: 'ON_PAGE_LOADED', bookmark: currResult[this.bookmarkFieldName] });

                return prevClone;
            }
        })
    }

    getLoadArgs = (state: State, dispatch: Dispatch<Action>): LoadArgs => {

        /**
         * Dispatches state actions when certain network status changes happen.
         */
        this.sideEffect = {
            deps: [this.queryResult.networkStatus, state.currNetStat, state.prevNetStat],
            callback: () => {
                if (state.currNetStat !== this.queryResult.networkStatus){
                    // Update query network status
                    dispatch({ type: 'UPDATE_NETWORK_STATUS', status: this.queryResult.networkStatus });
                }
                if (state.currNetStat === NetworkStatus.ready && state.prevNetStat === NetworkStatus.loading){
                    // Query just finished loading
                    const result = this.getMangoQueryResult(this.queryResult.data);
                    if (!result){
                        console.warn("MangoQueryResult is null or undefined in BookmarkAdapter.");
                        return;
                    }
                    if (result[this.bookmarkFieldName] === undefined){
                        console.warn(
                            "Bookmark is undefined in MangoQueryResult. " +
                            "Make sure you have selected the 'bookmark' field in your query!"
                        );
                    }
                    dispatch({ type: 'ON_PAGE_LOADED', bookmark: result[this.bookmarkFieldName] });
                }
                if (
                    (state.currNetStat === NetworkStatus.ready && state.prevNetStat === NetworkStatus.refetch) ||
                    (state.currNetStat === NetworkStatus.ready && state.prevNetStat === NetworkStatus.setVariables)
                ){
                    // Query just finished reloading due to refetch or variable change
                    const result = this.getMangoQueryResult(this.queryResult.data);
                    dispatch({ type: 'INIT_BOOKMARK' });
                    dispatch({ type: 'ON_PAGE_LOADED', bookmark: result[this.bookmarkFieldName] });
                }
            }
        }

        // If the current and last bookmarks are the same, then we have reached the last page.
        const hasNextPage = state.lastBookmark !== state.currentBookmark && state.currentBookmark !== null;

        return {
            ...super.getLoadArgs(state, dispatch),
            hasNextPage: hasNextPage
        }
    }
}